import { useMemo } from "react";

import {
  FileOutlined,
  DollarOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { Column, Pie } from "@ant-design/plots";
import { useQuery } from "@tanstack/react-query";
import {
  Col,
  Typography,
  Card,
  Statistic,
  Tooltip,
  Row,
  DatePicker,
  Select,
  Space,
} from "antd";

import dayjs from "../../../config/dayjs";
import { getBranchList } from "../../../general/api/branch.api";
import useDeviceType from "../../../utils/deviceType";
import { formatCompactRM, formatRM } from "../../../utils/numberFormat";
import { styles } from "../styles/dashboardStyles";
import { Dashboard } from "../type/dashboard";

const { Text } = Typography;

interface AdminDashboardProps {
  dashboard: Dashboard;
  selectedBranchId?: string;
  onBranchIdChange?: (branchId: string) => void;
  selectedYear?: number;
  onYearChange?: (year: number) => void;
}

const AdminDashboardCards = ({
  dashboard,
  selectedBranchId,
  onBranchIdChange,
  selectedYear,
  onYearChange,
}: AdminDashboardProps) => {
  const { isMobile } = useDeviceType();

  const cardStyle = {
    ...styles.card,
    margin: "10px 0",
    height: 200,
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    border: "1px solid #f0f0f0",
  };

  /* ================ Branch Data ================ */
  const { data: branchData } = useQuery({
    queryKey: ["branches"],
    queryFn: getBranchList,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const branchOptions =
    branchData?.results.map((branch: { id: string; branchName: string }) => ({
      value: branch.id,
      label: branch.branchName,
    })) || [];
  /* ================ End Branch Data ================ */

  const pieData = useMemo(() => {
    const raw = dashboard?.fileCounts as any;
    const totalFiles = raw?.totalFiles ?? 0;
    const completedFiles = raw?.completedFiles ?? 0;
    const incompleteFiles = Math.max(totalFiles - completedFiles, 0);
    return [
      { type: "Completed", value: completedFiles },
      { type: "Incomplete", value: incompleteFiles },
    ];
  }, [dashboard]);

  return (
    <>
      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={8} xl={6}>
            <Card
              // hoverable
              size="small"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <FileOutlined
                    style={{
                      fontSize: "18px",
                      color: "#1890ff",
                      marginRight: "8px",
                    }}
                  />
                  <span>Total Files ({dashboard?.month})</span>
                </div>
              }
              style={cardStyle}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  padding: "16px",
                }}
              >
                <Statistic
                  value={dashboard?.totalFile || 0}
                  valueStyle={{
                    color: "#1890ff",
                    fontSize: "28px",
                    fontWeight: "bold",
                  }}
                  prefix={<FileOutlined />}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: "14px",
                    marginTop: "8px",
                    textAlign: "center",
                  }}
                >
                  Total files processed this month
                </Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={8} xl={6}>
            <Card
              // hoverable
              size="small"
              title={
                <div style={{ display: "flex", alignItems: "center" }}>
                  <DollarOutlined
                    style={{
                      fontSize: "18px",
                      color: "#52c41a",
                      marginRight: "8px",
                    }}
                  />
                  <span>
                    SPA/Loan/Finance/Tenancy/Contract Amount ({dashboard?.month}
                    )
                  </span>
                </div>
              }
              style={cardStyle}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  padding: "16px",
                }}
              >
                <Statistic
                  value={dashboard?.totalSPAPrice || 0}
                  valueStyle={{
                    color: "#52c41a",
                    fontSize: "28px",
                    fontWeight: "bold",
                  }}
                  formatter={(value) => formatCompactRM(Number(value))}
                  valueRender={() => (
                    <Tooltip title={formatRM(dashboard?.totalSPAPrice || 0)}>
                      {formatCompactRM(dashboard?.totalSPAPrice || 0)}
                    </Tooltip>
                  )}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: "14px",
                    marginTop: "8px",
                    textAlign: "center",
                  }}
                >
                  Total SPA value for the month
                </Text>
              </div>
            </Card>
          </Col>
        </Row>
      </Col>

      <Col span={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} lg={24} xl={12}>
            <Card
              size="small"
              title={
                isMobile ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      padding: "0 0 16px 0",
                      gap: "8px",
                    }}
                  >
                    <span>SPA Price Overview</span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        width: "100%",
                        flexWrap: "wrap",
                        gap: "8px",
                      }}
                    >
                      <Space
                        size="middle"
                        wrap
                        style={{
                          width: "100%",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Select
                          placeholder="Select Branch"
                          value={selectedBranchId}
                          onChange={(value) =>
                            onBranchIdChange && onBranchIdChange(value)
                          }
                          options={branchOptions}
                          allowClear
                          style={{
                            minWidth: 160,
                            maxWidth: 200,
                            borderRadius: "6px",
                          }}
                        />
                        <DatePicker
                          picker="year"
                          value={dayjs(`${selectedYear}-01-01`)}
                          onChange={(date) =>
                            onYearChange &&
                            onYearChange(date ? date.year() : dayjs().year())
                          }
                          allowClear={false}
                          format="YYYY"
                          style={{
                            minWidth: 120,
                            maxWidth: 140,
                            borderRadius: "6px",
                          }}
                          suffixIcon={
                            <CalendarOutlined style={{ color: "#1890ff" }} />
                          }
                        />
                      </Space>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "0 0 16px 0",
                    }}
                  >
                    <span>SPA Price Overview</span>
                    <Space size="middle">
                      <Select
                        placeholder="Select Branch"
                        value={selectedBranchId}
                        onChange={(value) =>
                          onBranchIdChange && onBranchIdChange(value)
                        }
                        options={branchOptions}
                        allowClear
                        style={{
                          width: 160,
                          borderRadius: "6px",
                        }}
                      />
                      <DatePicker
                        picker="year"
                        value={dayjs(`${selectedYear}-01-01`)}
                        onChange={(date) =>
                          onYearChange &&
                          onYearChange(date ? date.year() : dayjs().year())
                        }
                        allowClear={false}
                        format="YYYY"
                        style={{
                          width: 120,
                          borderRadius: "6px",
                        }}
                        suffixIcon={
                          <CalendarOutlined style={{ color: "#1890ff" }} />
                        }
                      />
                    </Space>
                  </div>
                )
              }
              style={{
                ...styles.card,
                margin: "10px 0",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                border: "1px solid #f0f0f0",
                padding: 16,
                minHeight: 400,
              }}
            >
              <Column
                data={dashboard?.spaPriceInYear || []}
                xField="month"
                yField="total"
                columnWidthRatio={0.5}
                color="#52c41a"
                xAxis={{
                  title: { text: "Month" },
                  label: { autoRotate: true, autoHide: true },
                }}
                yAxis={{
                  title: { text: "SPA Price (RM)" },
                  label: {
                    formatter: (v) => formatCompactRM(Number(v)),
                  },
                  nice: true,
                }}
                tooltip={{
                  formatter: (d) => ({
                    name: "SPA Price",
                    value: `RM ${Number(d.total).toLocaleString()}`,
                  }),
                }}
                autoFit
                height={320}
                label={false}
                meta={{
                  month: { alias: "Month" },
                  total: { alias: "SPA Price" },
                }}
                interactions={[{ type: "element-active" }]}
                padding={[24, 24, 48, 72]}
                appendPadding={8}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} lg={16} xl={12}>
            <Card
              size="small"
              title={
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "0 0 16px 0",
                  }}
                >
                  <span>Files by Status</span>
                </div>
              }
              style={{
                ...styles.card,
                margin: "10px 0",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                border: "1px solid #f0f0f0",
                padding: "20px 16px",
                minHeight: 400,
              }}
            >
              <Pie
                data={pieData}
                angleField="value"
                colorField="type"
                radius={0.9}
                label={{
                  type: "inner",
                  content: ({ percent }) => `${(percent * 100).toFixed(0)}%`,
                }}
                legend={{ position: "bottom" }}
                tooltip={{
                  formatter: (d) => ({ name: d.type, value: d.value }),
                }}
                color={["#52c41a", "#ff4d4f"]}
                interactions={[{ type: "element-active" }]}
                statistic={{
                  title: false,
                  content: {
                    content: `${dashboard?.month}`,
                  },
                }}
                height={320}
                padding={[24, 24, 48, 48]}
                appendPadding={8}
              />
            </Card>
          </Col>
        </Row>
      </Col>
    </>
  );
};

export default AdminDashboardCards;
